import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useGoogleConnection } from "./useGoogleConnection";
import { useAuth } from "@/contexts/AuthContext";
import { useRepository } from "@/contexts/PlatformContext";

import { openInSystemBrowser, isPwaMode } from "@/utils/pwa";
import { gatherBackupPayload, type ManuscriptBackupPayload } from "@/lib/backupPayload";
import type { Manuscript } from "@/types/manuscript";

interface DriveFile {
  id: string;
  name: string;
  modifiedTime: string;
}

// Comprehensive Drive data — mirrors the Supabase backup payload
interface ManuscriptDriveData {
  id: string;
  title: string;
  wordCount: number;
  lastModified: string;
  userId?: string;
  metadata: {
    authorName?: string;
    defaultPOV?: string;
    defaultTense?: string;
  };
  // Full backup payload (codex, planner, scenes, goals, format)
  payload: ManuscriptBackupPayload;
  // Schema version for forward-compatibility
  schemaVersion: number;
}

// Legacy format (v1) for backward-compatible reads
interface LegacyManuscriptDriveData {
  id: string;
  title: string;
  content: string;
  wordCount: number;
  lastModified: string;
  metadata: {
    authorName?: string;
    defaultPOV?: string;
    defaultTense?: string;
  };
  schemaVersion?: undefined;
}

type AnyManuscriptDriveData = ManuscriptDriveData | LegacyManuscriptDriveData;

export function useGoogleDrive() {
  const { user } = useAuth();
  const repository = useRepository();
  const { isConnected, disconnect, refetch: refetchConnection } = useGoogleConnection();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<"idle" | "syncing" | "error" | "success">("idle");
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  
  // Store access token in memory (not persisted)
  const accessTokenRef = useRef<string | null>(null);
  const tokenExpiresAtRef = useRef<number | null>(null);

  // Check URL for OAuth callback result
  useEffect(() => {
    const url = new URL(window.location.href);
    const connected = url.searchParams.get("google_connected");
    const error = url.searchParams.get("google_error");
    
    if (connected === "true") {
      const hash = window.location.hash;
      if (hash) {
        const params = new URLSearchParams(hash.slice(1));
        const token = params.get("access_token");
        const expiresIn = params.get("expires_in");
        
        if (token) {
          accessTokenRef.current = token;
          tokenExpiresAtRef.current = Date.now() + (parseInt(expiresIn || "3600") * 1000);
        }
      }
      
      url.searchParams.delete("google_connected");
      window.history.replaceState({}, "", url.pathname);
      refetchConnection();
    } else if (error) {
      setConnectionError(error);
      url.searchParams.delete("google_error");
      window.history.replaceState({}, "", url.pathname);
    }
  }, [refetchConnection]);

  // Get a valid access token (refresh if needed)
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (
      accessTokenRef.current &&
      tokenExpiresAtRef.current &&
      Date.now() < tokenExpiresAtRef.current - 300000
    ) {
      return accessTokenRef.current;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const response = await supabase.functions.invoke("google-refresh-token", {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (response.error || !response.data?.access_token) {
      console.error("Failed to refresh token:", response.error);
      let errorMsg = "Google connection is invalid or expired. Please reconnect Google Drive in Settings.";
      if (response.error && response.error.message) {
        errorMsg = response.error.message;
      }
      throw new Error(errorMsg);
    }

    accessTokenRef.current = response.data.access_token;
    tokenExpiresAtRef.current = Date.now() + (response.data.expires_in * 1000);
    
    return accessTokenRef.current;
  }, []);

  // Initiate Google OAuth connection
  const connect = useCallback(async (redirectUrl?: string) => {
    setIsConnecting(true);
    setConnectionError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      const response = await supabase.functions.invoke("google-auth-start", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {
          redirectUrl: redirectUrl || (window.location.origin + "/settings"),
        },
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to start OAuth");
      }

      if (response.data?.url) {
        openInSystemBrowser(response.data.url);
        
        if (isPwaMode()) {
          setTimeout(() => {
            setIsConnecting(false);
          }, 2000);
        }
      }
    } catch (error) {
      setConnectionError(error instanceof Error ? error.message : "Connection failed");
      setIsConnecting(false);
    }
  }, []);

  // Get app folder ID (create if doesn't exist)
  const getAppFolderId = useCallback(async (accessToken: string): Promise<string | null> => {
    if (!user?.id) return null;
    const folderName = `Birch_${user.id}`;

    const searchResponse = await fetch(
      "https://www.googleapis.com/drive/v3/files?" +
        new URLSearchParams({
          q: `name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
          spaces: "drive",
          fields: "files(id, name)",
        }),
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    const searchData = await searchResponse.json();
    
    if (searchData.files?.length > 0) {
      return searchData.files[0].id;
    }

    const createResponse = await fetch("https://www.googleapis.com/drive/v3/files", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: folderName,
        mimeType: "application/vnd.google-apps.folder",
      }),
    });

    const createData = await createResponse.json();
    return createData.id || null;
  }, [user?.id]);

  // Find file by manuscript ID in the app folder
  const findFileByManuscriptId = useCallback(
    async (accessToken: string, folderId: string, manuscriptId: string): Promise<DriveFile | null> => {
      const response = await fetch(
        "https://www.googleapis.com/drive/v3/files?" +
          new URLSearchParams({
            q: `'${folderId.replace(/'/g, "\\'")}' in parents and name='${manuscriptId.replace(/'/g, "\\'")}.json' and trashed=false`,
            spaces: "drive",
            fields: "files(id, name, modifiedTime)",
          }),
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const data = await response.json();
      return data.files?.[0] || null;
    },
    []
  );

  // Upload comprehensive manuscript data to Google Drive
  const uploadManuscript = useCallback(
    async (accessToken: string, folderId: string, manuscript: Manuscript, existingFileId?: string) => {
      // Gather comprehensive payload (same scope as Supabase backup)
      const payload = await gatherBackupPayload(manuscript.id, repository);
      
      const driveData: ManuscriptDriveData = {
        id: manuscript.id,
        title: manuscript.title,
        wordCount: manuscript.wordCount,
        lastModified: manuscript.lastModified.toISOString(),
        userId: user?.id,
        metadata: {
          authorName: manuscript.authorName,
          defaultPOV: manuscript.defaultPOV,
          defaultTense: manuscript.defaultTense,
        },
        payload,
        schemaVersion: 2,
      };

      const boundary = "deckle_boundary_" + Date.now();
      const metadata = {
        name: `${manuscript.id}.json`,
        mimeType: "application/json",
        ...(existingFileId ? {} : { parents: [folderId] }),
      };

      const multipartBody =
        `--${boundary}\r\n` +
        `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
        JSON.stringify(metadata) +
        `\r\n--${boundary}\r\n` +
        `Content-Type: application/json\r\n\r\n` +
        JSON.stringify(driveData) +
        `\r\n--${boundary}--`;

      const url = existingFileId
        ? `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=multipart`
        : "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart";

      const response = await fetch(url, {
        method: existingFileId ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": `multipart/related; boundary=${boundary}`,
        },
        body: multipartBody,
      });

      if (!response.ok) {
        throw new Error("Failed to upload manuscript");
      }

      return response.json();
    },
    [user?.id, repository]
  );

  // Download manuscript from Google Drive (handles both v1 and v2 formats)
  const downloadManuscript = useCallback(
    async (accessToken: string, fileId: string): Promise<AnyManuscriptDriveData | null> => {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!response.ok) return null;
      return response.json();
    },
    []
  );

  // Sync a single manuscript
  const syncManuscript = useCallback(
    async (manuscript: Manuscript): Promise<void> => {
      if (!isConnected) return;

      setSyncStatus("syncing");

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          throw new Error("Failed to get access token");
        }

        const folderId = await getAppFolderId(accessToken);
        if (!folderId) {
          throw new Error("Failed to get app folder");
        }

        const existingFile = await findFileByManuscriptId(accessToken, folderId, manuscript.id);

        if (existingFile) {
          const driveModified = new Date(existingFile.modifiedTime);
          const localModified = manuscript.lastModified;

          if (localModified > driveModified) {
            await uploadManuscript(accessToken, folderId, manuscript, existingFile.id);
          }
        } else {
          await uploadManuscript(accessToken, folderId, manuscript);
        }

        setLastSyncTime(new Date());
        setSyncStatus("success");
        setTimeout(() => setSyncStatus("idle"), 3000);
      } catch (error) {
        console.error("Sync error:", error);
        setSyncStatus("error");
      }
    },
    [isConnected, getAccessToken, getAppFolderId, findFileByManuscriptId, uploadManuscript]
  );

  // Pull manuscript from Drive
  const pullFromDrive = useCallback(
    async (manuscriptId: string): Promise<AnyManuscriptDriveData | null> => {
      if (!isConnected) return null;

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) return null;

        const folderId = await getAppFolderId(accessToken);
        if (!folderId) return null;

        const file = await findFileByManuscriptId(accessToken, folderId, manuscriptId);
        if (!file) return null;

        return downloadManuscript(accessToken, file.id);
      } catch (error) {
        console.error("Pull error:", error);
        return null;
      }
    },
    [isConnected, getAccessToken, getAppFolderId, findFileByManuscriptId, downloadManuscript]
  );

  // Find legacy "Deckle" folder if it exists
  const getLegacyFolderId = useCallback(async (accessToken: string): Promise<string | null> => {
    const searchResponse = await fetch(
      "https://www.googleapis.com/drive/v3/files?" +
        new URLSearchParams({
          q: "name='Deckle' and mimeType='application/vnd.google-apps.folder' and trashed=false",
          spaces: "drive",
          fields: "files(id, name)",
        }),
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    const searchData = await searchResponse.json();
    return searchData.files?.[0]?.id || null;
  }, []);

  // List all manuscripts from Drive
  const listDriveManuscripts = useCallback(async (customFolderId?: string): Promise<DriveFile[]> => {
    if (!isConnected) return [];

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) return [];

      const folderId = customFolderId || await getAppFolderId(accessToken);
      if (!folderId) return [];

      const response = await fetch(
        "https://www.googleapis.com/drive/v3/files?" +
          new URLSearchParams({
            q: `'${folderId.replace(/'/g, "\\'")}' in parents and name contains '.json' and trashed=false`,
            spaces: "drive",
            fields: "files(id, name, modifiedTime)",
          }),
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const data = await response.json();
      return data.files || [];
    } catch (error) {
      console.error("List error:", error);
      return [];
    }
  }, [isConnected, getAccessToken, getAppFolderId]);

  return {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    syncManuscript,
    pullFromDrive,
    listDriveManuscripts,
    lastSyncTime,
    syncStatus,
    getAccessToken,
    uploadManuscript,
    getAppFolderId,
    findFileByManuscriptId,
    getLegacyFolderId,
    downloadManuscript,
  };
}
